import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import EventIcon from "@mui/icons-material/Event";
import TodayIcon from "@mui/icons-material/Today";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { BACKEND_URI } from "../constants";
import { DailyInfo, Event, EventInfo, OperationInfo } from "../model/Types";
import AuthStore from "../stores/AuthStore";
import Fetch from "../api/Fetch";
import Toaster from "../common/Toaster";
import { DateTime } from "luxon";
import { DatePicker } from "@mui/x-date-pickers";

const EventInfoView = () => {
    const [expanded, setExpanded] = React.useState<string | false>("today");
    const [dailyInfo, setDailyInfo] = React.useState<string | undefined>(undefined);
    const [eventInfo, setEventInfo] = React.useState<string | undefined>(undefined);
    const [operationInfo, setOperationInfo] = React.useState<string | undefined>(undefined);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [date, setDate] = React.useState<string>(DateTime.now().toFormat("yyyy-MM-dd"));

    const cb = React.useCallback(async () => {
        setLoading(true);
        try {
            const body = await Fetch<{
                dailyInfo: string;
                eventInfo: string;
                operationInfo: string;
            }>(`${BACKEND_URI}/event-daily-info?date=${date}`, {
                includeAuth: true,
            });
            setDailyInfo(body.dailyInfo);
            setEventInfo(body.eventInfo);
            setOperationInfo(body.operationInfo);
        } catch (err: any) {
            Toaster.error(err?.message ?? "An error occurred while retrieving current event.");
            console.log(err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1_000);
        }
    }, [date]);

    React.useEffect(() => {
        cb();
    }, [cb]);

    const handleChange = (panel: string) => (event: any, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };
    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Event Information
            </Typography>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <DatePicker
                            label="Select Date"
                            value={DateTime.fromFormat(date, "yyyy-MM-dd")}
                            onChange={(newValue) => {
                                if (newValue) {
                                    setDate(newValue.toFormat("yyyy-MM-dd"));
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: <CalendarMonthIcon sx={{ mr: 1, color: "action.active" }} />,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: { xs: "left", md: "right" } }}>
                        <Button
                            variant="contained"
                            onClick={() => cb()}
                            disabled={loading || !AuthStore.loggedIn}
                            startIcon={<RefreshIcon />}
                            sx={{ borderRadius: 2 }}
                        >
                            Refresh Information
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Accordion
                        expanded={expanded === "today"}
                        onChange={handleChange("today")}
                        elevation={2}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            "&:before": { display: "none" },
                            overflow: "hidden",
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: "primary.light",
                                color: "text.primary",
                                "&:hover": { backgroundColor: "primary.main" },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <TodayIcon />
                                <Typography variant="h6">Today's Information</Typography>
                                {/* {expanded === "today" && !loading && dailyInfo && (
                                    <Chip 
                                        label="Active" 
                                        size="small" 
                                        sx={{ 
                                            ml: 2,
                                            backgroundColor: 'success.main',
                                            color: 'success.contrastText'
                                        }}
                                    />
                                )} */}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: "background.paper", p: 3 }}>
                            {loading ? (
                                <Box sx={{ py: 2 }}>
                                    <LinearProgress />
                                </Box>
                            ) : (
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: "action.hover", borderRadius: 1 }}>
                                    <div
                                        className="view ql-editor"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                dailyInfo ??
                                                "<div style='color: gray; font-style: italic;'>No information available for this date.</div>",
                                        }}
                                        style={{ margin: "0px", padding: "0px" }}
                                    />
                                </Paper>
                            )}
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expanded === "event"}
                        onChange={handleChange("event")}
                        elevation={2}
                        sx={{
                            borderRadius: 2,
                            "&:before": { display: "none" },
                            overflow: "hidden",
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: "primary.light",
                                color: "text.primary",
                                "&:hover": { backgroundColor: "primary.main" },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <EventIcon />
                                <Typography variant="h6">Event Information</Typography>
                                {/* {expanded === "event" && !loading && eventInfo && (
                                    <Chip
                                        label="Active"
                                        size="small"
                                        sx={{
                                            ml: 2,
                                            backgroundColor: "success.main",
                                            color: "success.contrastText",
                                        }}
                                    />
                                )} */}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: "background.paper", p: 3 }}>
                            {loading ? (
                                <Box sx={{ py: 2 }}>
                                    <LinearProgress />
                                </Box>
                            ) : (
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: "action.hover", borderRadius: 1 }}>
                                    <div
                                        className="view ql-editor"
                                        style={{ margin: "0px", padding: "0px" }}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                eventInfo ?? "<div style='color: gray; font-style: italic;'>No event information available.</div>",
                                        }}
                                    />
                                </Paper>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ borderRadius: 2, height: "100%" }}>
                        <CardHeader
                            title="Operation Information"
                            titleTypographyProps={{ variant: "h6" }}
                            avatar={<NotificationsIcon />}
                            sx={{
                                backgroundColor: "primary.light",
                                color: "text.primary",
                            }}
                        />
                        <Divider />
                        <CardContent sx={{ p: 3 }}>
                            {loading ? (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: "action.hover", borderRadius: 1 }}>
                                    <div
                                        className="view ql-editor"
                                        style={{ margin: "0px", padding: "0px" }}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                operationInfo ??
                                                "<div style='color: gray; font-style: italic; text-align: center;'>No operation notes available.</div>",
                                        }}
                                    />
                                </Paper>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default EventInfoView;
